import { ColumnDef } from '@tanstack/react-table';
import { format } from 'date-fns';
import { AdvanceableStudent } from '@/entities/Courses';
import { IndeterminateCheckbox } from '@/shared/ui/IndeterminateCheckbox';
import { Svg } from '@/shared/ui/Svg';
import { Chip } from '@heroui/react';
import ChevronDownIcon from '@/shared/assets/icons/chevron-down.svg';

const getStatus = (student: AdvanceableStudent): 'Pass' | 'Failed' => {
  const attendance = student.attendancePercent ?? 0;
  const progress = student.currentTermProgressByLessons ?? 0;
  if (attendance >= 70 && progress >= 70) {
    return 'Pass';
  }
  return 'Failed';
};

export const advanceableStudentsColumns = (): ColumnDef<AdvanceableStudent>[] => [
  {
    id: 'select',
    header: ({ table }) => (
      <IndeterminateCheckbox
        uniqueKey="all-advanceable-select"
        name="all-advanceable"
        checked={table.getIsAllRowsSelected()}
        indeterminate={table.getIsSomeRowsSelected()}
        onChange={table.getToggleAllRowsSelectedHandler()}
      />
    ),
    cell: ({ row }) => (
      <IndeterminateCheckbox
        uniqueKey={`advanceable-select-${row.original.id}`}
        name="advanceable"
        checked={row.getIsSelected()}
        disabled={!row.getCanSelect()}
        onChange={row.getToggleSelectedHandler()}
      />
    ),
  },
  {
    header: () => (
      <div className="flex items-center gap-2">
        <span>ID</span>
        <Svg Icon={ChevronDownIcon} width={12} height={12} className="text-grey-secondary" />
      </div>
    ),
    accessorKey: 'id',
    cell: ({ row }) => (
      <span className="text-black-headline">{row.original.username || row.original.id}</span>
    ),
  },
  {
    header: 'User',
    accessorFn: ({ firstName, lastName }) => `${firstName} ${lastName}`,
    cell: ({ row }) => (
      <div className="flex flex-col">
        <span className="text-black-headline font-medium">
          {row.original.firstName} {row.original.lastName}
        </span>
        <span className="text-grey-secondary text-sm">{row.original.email}</span>
      </div>
    ),
  },
  {
    header: () => (
      <div className="flex items-center gap-2">
        <span>Registation Date</span>
        <Svg Icon={ChevronDownIcon} width={12} height={12} className="text-grey-secondary" />
      </div>
    ),
    accessorKey: 'registrationDate',
    cell: ({ row }) => {
      // TODO: Add registrationDate to API response
      return <span className="text-grey-secondary">—</span>;
    },
  },
  {
    header: () => (
      <div className="flex items-center gap-2">
        <span>Status</span>
        <Svg Icon={ChevronDownIcon} width={12} height={12} className="text-grey-secondary" />
      </div>
    ),
    accessorKey: 'status',
    cell: ({ row }) => {
      const status = getStatus(row.original);
      return (
        <Chip
          className={`${
            status === 'Pass' ? 'bg-green-positive text-green-tertiary' : 'bg-red-secondary text-red-tertiary'
          } text-xs flex items-center border-0 rounded-[4px] py-1 px-1.5`}
          variant="flat"
          classNames={{
            base: `${
              status === 'Pass' ? 'bg-green-positive' : 'bg-red-secondary'
            } border-0 rounded-[4px] py-1 px-1.5`,
            content: `${
              status === 'Pass' ? 'text-green-tertiary' : 'text-red-tertiary'
            } text-xs flex items-center`,
          }}
        >
          <span className="normal-case">{status}</span>
        </Chip>
      );
    },
  },
];

